import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/** @typedef {HTMLElement & { render?: () => void }} WishlistToggle */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {number | null} */
  #visibilityFrame = null;

  /** @type {EventTarget | null} */
  #scrollEventTarget = null;

  /**
   * The shortcut is intentionally available at the bottom on a fresh PDP
   * visit. Once the shopper starts scrolling, visibility follows the live
   * position of the primary add-to-cart control.
   *
   * @type {boolean}
   */
  #hasUserScrolled = false;

  connectedCallback() {
    // Shopify's editor can detach and reattach an existing section element.
    // Recreate the controller after a prior disconnect so its listeners are
    // bound to the live section again.
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    super.connectedCallback();

    if (this.#supportsDialogSuppression()) {
      this.#setupDialogVisibilityObserver();
    }

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    this.#bindScrollListener();
    window.addEventListener('resize', this.#queueStickyVisibility, { passive: true, signal });
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange, { signal });

    // The sticky markup can arrive before the live product form in editor and
    // section-rendered responses. Rebind on the next frame for both PDPs.
    requestAnimationFrame(() => {
      if (!signal.aborted) this.#setupIntersectionObserver();
    });

    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('giesto:variant-change', this.#handleBasicVariantChange, { signal });

    this.#getInitialQuantity();

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#scrollEventTarget?.removeEventListener('scroll', this.#handleUserScroll);
    this.#scrollEventTarget = null;
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#visibilityFrame) cancelAnimationFrame(this.#visibilityFrame);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#buyButtonsIntersectionObserver = null;
    this.#targetAddToCartButton = null;

    const productForm = this.#getProductForm();
    if (!productForm) {
      this.#syncStickyVisibility();
      return;
    }

    // The reference can be replaced after a variant update. Start each setup
    // from the live product form and release any observer from an older DOM
    // node before binding the new one.
    this.#targetAddToCartButton = productForm.querySelector(
      '[ref="addToCartButton"][type="submit"], button[type="submit"][name="add"]'
    );

    const visibilityTarget = this.#targetAddToCartButton;
    if (!visibilityTarget) {
      this.#syncStickyVisibility();
      return;
    }

    this.#refreshPrimaryCtaState();
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      () => this.#refreshPrimaryCtaState(),
      { root: getIntersectionRoot(), threshold: 0 }
    );

    this.#buyButtonsIntersectionObserver.observe(visibilityTarget);

  }

  #bindScrollListener() {
    const nextTarget = getScrollEventTarget();
    if (this.#scrollEventTarget === nextTarget) return;

    this.#scrollEventTarget?.removeEventListener('scroll', this.#handleUserScroll);
    nextTarget.addEventListener('scroll', this.#handleUserScroll, {
      passive: true,
      signal: this.#abortController.signal,
    });
    this.#scrollEventTarget = nextTarget;
  }

  #handleScrollContainerChange = () => {
    if (this.#abortController.signal.aborted) return;

    this.#bindScrollListener();
    this.#setupIntersectionObserver();
    this.#queueStickyVisibility();
  };

  #handleUserScroll = () => {
    this.#hasUserScrolled = true;
    this.#queueStickyVisibility();
  };

  #queueStickyVisibility = () => {
    if (this.#visibilityFrame) return;

    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = null;
      this.#refreshPrimaryCtaState();
    });
  };

  #refreshPrimaryCtaState() {
    this.#syncStickyVisibility();
  }

  #getStickyPosition(rect) {
    if (!rect || rect.width <= 0 || rect.height <= 0) return 'hidden';

    // On a fresh PDP visit the shortcut starts at the bottom by design. It
    // then hands off to the on-page CTA as soon as the shopper begins
    // scrolling, even if that CTA happened to be visible at first paint.
    if (!this.#hasUserScrolled) return 'bottom';

    const { top: scrollTop, bottom: scrollBottom } = this.#getVisibleScrollBounds();
    const visibleTop = Math.max(scrollTop, this.#headerBottom());
    const currentPosition = this.dataset.stickyPosition;
    const edgeTolerance = 8;

    if (scrollBottom <= visibleTop) return 'hidden';

    // Keep the last physical edge for a few pixels around the hand-off
    // boundary. Header measurement and sub-pixel image layout can otherwise
    // make the shortcut flicker while the primary CTA touches an edge.
    if (currentPosition === 'top' && rect.bottom <= visibleTop + edgeTolerance) return 'top';

    if (currentPosition === 'bottom' && rect.top >= scrollBottom - edgeTolerance) return 'bottom';

    if (rect.bottom <= visibleTop) return 'top';

    if (rect.top < scrollBottom && rect.bottom > visibleTop) return 'hidden';

    return 'bottom';
  }

  #getVisibleScrollBounds() {
    const root = getIntersectionRoot();
    if (!(root instanceof Element)) {
      return { top: 0, bottom: window.innerHeight };
    }

    const rect = root.getBoundingClientRect();
    return {
      top: Math.max(0, rect.top),
      bottom: Math.min(window.innerHeight, rect.bottom),
    };
  }

  #headerBottom() {
    const header = document.querySelector('#header-group, giesto-site-header, giesto-header, .giesto-header, [data-header-group]');
    if (!(header instanceof HTMLElement)) return 0;

    return Math.max(0, Math.min(window.innerHeight, header.getBoundingClientRect().bottom));
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const productForm = this.#getProductForm();
    const productFormElement =
      productForm?.querySelector('form[data-type="add-to-cart-form"]') ?? productForm?.querySelector('form');
    const targetAddToCartButton = /** @type {HTMLButtonElement | null} */ (
      productFormElement?.querySelector('[ref="addToCartButton"][type="submit"], button[type="submit"][name="add"]') ??
        productForm?.querySelector('[ref="addToCartButton"][type="submit"], button[type="submit"][name="add"]')
    );

    if (!(productFormElement instanceof HTMLFormElement) || !(targetAddToCartButton instanceof HTMLButtonElement)) return;
    if (targetAddToCartButton.disabled) return;

    this.#targetAddToCartButton = targetAddToCartButton;

    if (!this.#submitPrimaryProductForm(productForm, productFormElement, targetAddToCartButton)) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // The GIESTO PDPs use a quiet press/release confirmation. They should not
    // launch a second visual treatment from the sticky bar after the real form
    // has already updated the cart and opened its confirmation surface.
    if (this.dataset.disableFlyToCart === 'true' || !cartIcon || !this.refs.addToCartButton || !this.refs.productImage) {
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 220);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #submitPrimaryProductForm(productForm, form, submitter) {
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return false;

    if (typeof productForm.handleSubmit === 'function') {
      const submitEvent =
        typeof SubmitEvent === 'function'
          ? new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter })
          : new Event('submit', { bubbles: true, cancelable: true });

      productForm.handleSubmit(submitEvent);
      return true;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
      return true;
    }

    submitter.dataset.puppet = 'true';
    submitter.click();
    return true;
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // The wishlist store is the source of truth. Server-rendered variant
        // HTML starts unpressed, so restore the persisted active state after
        // morphing without waiting for another wishlist event.
        const wishlistToggle = /** @type {WishlistToggle | null} */ (
          this.refs.stickyBar.querySelector('giesto-wishlist-toggle')
        );
        wishlistToggle?.render?.();

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        this.#setupIntersectionObserver();
        requestAnimationFrame(() => {
          if (!this.#abortController.signal.aborted) this.#setupIntersectionObserver();
        });

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * The Basic PDP owns its variants in a lightweight custom element instead of
   * Shopify's variant-picker. Keep the sticky shortcut in sync through its
   * local event, without creating another product form or cart request.
   *
   * @param {CustomEvent<{ productId?: string|number, variant?: Record<string, any>|null, selectedOptions?: string[] }>} event
   */
  #handleBasicVariantChange = (event) => {
    if (!this.#isBasicPresentation()) return;

    const detail = event.detail;
    if (!detail || String(detail.productId ?? '') !== String(this.dataset.productId ?? '')) return;

    const variant = detail.variant ?? null;
    const available = Boolean(variant?.available);
    const selectedOptions = Array.isArray(detail.selectedOptions)
      ? detail.selectedOptions.filter((option) => typeof option === 'string' && option.trim()).join(' / ')
      : '';

    this.dataset.variantAvailable = String(available);
    this.dataset.currentVariantId = variant?.id ? String(variant.id) : '';
    this.refs.addToCartButton.disabled = !available;

    const label = this.querySelector('[data-gap-sticky-button-label]');
    if (label) {
      label.textContent = available
        ? this.dataset.addLabel || ''
        : variant
          ? this.dataset.soldOutLabel || ''
          : this.dataset.addLabel || '';
    }

    const variantTitle = this.querySelector('[data-gap-sticky-variant]');
    if (variantTitle) {
      variantTitle.textContent = selectedOptions;
      variantTitle.toggleAttribute('hidden', !selectedOptions);
    }

    if (variant) {
      const price = this.querySelector('[data-gap-sticky-price]');
      if (price) price.innerHTML = variant.price ?? '';

      const compare = this.querySelector('[data-gap-sticky-compare]');
      if (compare) {
        compare.innerHTML = variant.compare_at ?? '';
        compare.toggleAttribute('hidden', !variant.compare_at);
      }

      const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[ref="productImage"]'));
      if (image && variant.media_url) image.src = variant.media_url;
    }

    this.#updateButtonText();
    this.#queueStickyVisibility();
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#isStuck) return;

    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    if (!this.#isStuck) return;

    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #setupDialogVisibilityObserver() {
    if (!this.#supportsDialogSuppression()) return;

    this.#dialogObserver?.disconnect();
    this.#dialogObserver = new MutationObserver((records) => {
      if (this.#abortController.signal.aborted) return;
      if (!records.some((record) => record.target instanceof HTMLDialogElement)) return;
      this.#syncDialogVisibility();
    });
    this.#dialogObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true,
    });

    const { signal } = this.#abortController;
    document.addEventListener('toggle', this.#handleDocumentDialogStateChange, { capture: true, signal });
    document.addEventListener('close', this.#handleDocumentDialogStateChange, { capture: true, signal });
    document.addEventListener('cancel', this.#handleDocumentDialogStateChange, { capture: true, signal });

    this.#syncDialogVisibility();
  }

  #handleDocumentDialogStateChange = (event) => {
    if (!(event.target instanceof HTMLDialogElement) || !this.#supportsDialogSuppression()) return;

    requestAnimationFrame(() => {
      if (!this.#abortController.signal.aborted) this.#syncDialogVisibility();
    });
  };

  #syncDialogVisibility() {
    const dialogIsOpen = this.#hasOpenDialog();
    this.toggleAttribute('data-dialog-open', dialogIsOpen);

    this.#syncStickyVisibility();
  }

  #syncStickyVisibility() {
    const shouldShow = !this.#hasOpenDialog() && this.dataset.variantAvailable === 'true';
    const position = shouldShow ? this.#getStickyPosition(this.#targetAddToCartButton?.getBoundingClientRect()) : 'hidden';

    // `hidden` is a visibility state, not a physical edge. Keeping the last
    // top/bottom anchor prevents the bar from jumping to the opposite side of
    // the viewport while it fades out.
    if (position === 'hidden') {
      this.#hideStickyBar();
      return;
    }

    this.dataset.stickyPosition = position;
    this.#showStickyBar();
  }

  #hasOpenDialog() {
    return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => {
      const styles = getComputedStyle(dialog);
      const rect = dialog.getBoundingClientRect();

      return styles.display !== 'none' && styles.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    });
  }

  // Helper methods
  /**
   * Whether this instance is the GIESTO PDP presentation. Both product
   * templates share the same primary-CTA visibility rule, while this marker
   * is used only to opt into dialog suppression.
   *
   * @returns {boolean}
   */
  #isGiestoPresentation() {
    return this.classList.contains('giesto-product-information__sticky-add-to-cart');
  }

  /**
   * The Basic PDP has its own compact sticky treatment, but it uses the same
   * dialog suppression contract as the GIESTO PDP to prevent a CTA overlaying
   * a size guide, colour picker, or product-information drawer.
   */
  #isBasicPresentation() {
    return this.classList.contains('gap-sticky-add-to-cart');
  }

  #supportsDialogSuppression() {
    return this.#isGiestoPresentation() || this.#isBasicPresentation();
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const candidates = Array.from(sectionElement.querySelectorAll('product-form-component')).filter(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === productId && !candidate.closest('dialog')
    );

    const hasLiveAddToCartForm = (candidate) =>
      candidate.querySelector('form[data-type="add-to-cart-form"], form') instanceof HTMLFormElement &&
      candidate.querySelector('[ref="addToCartButton"][type="submit"], button[type="submit"][name="add"]') instanceof
        HTMLButtonElement;

    return (
      candidates.find(
        (candidate) =>
          candidate.closest('.giesto-product-information .product-details') &&
          !candidate.closest('giesto-product-upsell, giesto-product-cross-sell') &&
          hasLiveAddToCartForm(candidate)
      ) ??
      candidates.find(
        (candidate) =>
          !candidate.closest('giesto-product-upsell, giesto-product-cross-sell') && hasLiveAddToCartForm(candidate)
      ) ??
      null
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
